import { useState, type ReactNode } from "react";
import { Temporal } from "@js-temporal/polyfill";
import { AlertCircleIcon, CheckCircleIcon, DropletIcon, DropletOffIcon, EllipsisIcon, SparkleIcon, SunIcon } from "@/app/components/system/Icons";
import { SunHoursModal } from "@/app/components/SunHoursModal";
import { useWeatherAccent } from "@/app/components/weather/WeatherBanner";
import { usePotEffectiveSunHours, usePotGardenerRecommendations, usePots } from "@/app/lib/data-context";
import { formatSunHours } from "@/lib/sun-hours";
import { timelineKeyForOffset, type Pot, type WateringDay } from "@/types";

interface PotWateringRecommendationsProps {
  pot: Pot;
  /**
   * How many days the timeline shows. `2` (default) renders today + tomorrow,
   * as used in the library pot sections. `5` renders the full window — two days
   * ago through two days ahead, today centred — as used on the pot detail page.
   */
  days?: 2 | 5;
}

/** Day offsets rendered for each supported timeline length. */
function offsetsForDays(days: 2 | 5): number[] {
  return days === 5 ? [-2, -1, 0, 1, 2] : [0, 1];
}

export function PotWateringRecommendations({ pot, days = 2 }: PotWateringRecommendationsProps) {
  const { plan, loading, error } = usePotGardenerRecommendations(pot.id);
  const effectiveSunHours = usePotEffectiveSunHours(pot);
  const { color: todayAccent } = useWeatherAccent();
  const [sunOpen, setSunOpen] = useState(false);

  const hasSunWindow = !!pot.sunHours?.start && !!pot.sunHours?.end;
  const windowHours = sunWindowHours(pot.sunHours?.start, pot.sunHours?.end);

  // The recommendation failed and none is cached: keep the timeline (sun hours,
  // logged waterings) and surface the failure inline rather than hiding it all.
  const reasonLoading = !plan && loading;
  const reasonError = !plan && !loading && error;
  const hasReasonRow = reasonLoading || reasonError || !!plan?.reason;
  const offsets = offsetsForDays(days);

  return (
    <div className="flex flex-col">
      {offsets.map((offset, index) => {
        const key = timelineKeyForOffset(offset);
        const sunHours = key ? effectiveSunHours?.[key] ?? null : null;
        const wateredSource = key ? pot.timeline[key].watered ?? null : null;
        // Recommendations only exist for today and tomorrow; other days are null.
        const day = offset === 0 ? plan?.today ?? null : offset === 1 ? plan?.oneDayLater ?? null : null;
        return (
          <DayRow
            key={offset}
            offset={offset}
            day={day}
            sunHours={sunHours}
            hasSunWindow={hasSunWindow}
            windowHours={windowHours}
            wateredSource={wateredSource}
            pot={pot}
            onSetSunHours={() => setSunOpen(true)}
            topLine={index > 0}
            bottomLine={index < offsets.length - 1 || hasReasonRow}
            accentColor={todayAccent}
            recommendationLoading={reasonLoading}
            recommendationError={reasonError}
          />
        );
      })}

      {hasReasonRow ? (
        <div className="flex gap-3">
          <Rail
            nodeAlign="top"
            topLine={true}
            bottomLine={false}
            node={
              <span className="shrink-0 grid place-items-center w-4.5 h-4.5 rounded-full border border-terra bg-paper text-terra">
                {reasonLoading ? (
                  <span
                    className="w-2.5 h-2.5 rounded-full inline-block border-[1.5px] border-terra/30 border-t-terra animate-spin"
                    aria-hidden="true"
                  />
                ) : reasonError ? (
                  <AlertCircleIcon size={11} stroke={2} className="animate-[loc-fade_0.3s_ease]" />
                ) : (
                  <SparkleIcon size={10} className="animate-[loc-fade_0.3s_ease]" />
                )}
              </span>
            }
          />
          <div className="flex-1 min-w-0 py-2">
            {reasonLoading ? (
              <span className="flex flex-col gap-2 py-1" aria-hidden="true">
                <span className="skeleton h-2.5 rounded-full w-[88%]" />
                <span className="skeleton h-2.5 rounded-full w-[52%]" />
              </span>
            ) : reasonError ? (
              <p role="alert" className="m-0 font-sans text-[12.5px] text-terra leading-[1.4] animate-[loc-fade_0.3s_ease]">
                Couldn’t fetch watering recommendations. Pull to refresh in a moment.
              </p>
            ) : (
              <p className="m-0 font-display italic text-[12.5px] text-ink-2 leading-[1.4] animate-[loc-fade_0.35s_ease]">
                {plan?.reason}
              </p>
            )}
          </div>
        </div>
      ) : null}

      {sunOpen && <SunHoursModal pot={pot} onClose={() => setSunOpen(false)} />}
    </div>
  );
}

function Rail({ node, topLine, bottomLine, nodeAlign = "center" }: {
  node: ReactNode;
  topLine: boolean;
  bottomLine: boolean;
  nodeAlign?: "center" | "top";
}) {
  // "top" pins the node near the row's top (aligned with the first line of text);
  // "center" lets the equal flex-1 spacers center it vertically.
  const topSpacer = nodeAlign === "top" ? "h-2" : "flex-1";
  return (
    <div className="w-4 shrink-0 flex flex-col items-center">
      <span aria-hidden="true" className={`w-px ${topSpacer} ${topLine ? "bg-line" : "bg-transparent"}`} />
      {node}
      <span aria-hidden="true" className={`w-px flex-1 ${bottomLine ? "bg-line" : "bg-transparent"}`} />
    </div>
  );
}

type WaterState = "needed" | "watered" | "rained" | "skip" | "notWatered" | "none";

interface DayRowProps {
  offset: number;
  day: WateringDay | null;
  sunHours: number | null;
  hasSunWindow: boolean;
  windowHours: number | null;
  wateredSource: "user" | "rain" | null;
  onSetSunHours: () => void;
  topLine: boolean;
  bottomLine: boolean;
  accentColor?: string;
  pot: Pot;
  /** AI recommendation is still loading; the action slot shows a skeleton. */
  recommendationLoading: boolean;
  /** AI recommendation failed to load; the action slot shows an errored pill. */
  recommendationError: boolean;
}

function DayRow({ offset, day, sunHours, hasSunWindow, windowHours, wateredSource, onSetSunHours, topLine, bottomLine, accentColor, pot, recommendationLoading, recommendationError }: DayRowProps) {
  const isToday = offset === 0;

  return (
    <div className="flex gap-3">
      <Rail
        topLine={topLine}
        bottomLine={bottomLine}
        node={
          <span
            aria-hidden="true"
            className={`shrink-0 w-2.5 h-2.5 rounded-full ${isToday ? "" : "bg-paper border-[1.5px] border-line"}`}
            style={isToday ? { background: accentColor } : undefined}
          />
        }
      />

      <div className="flex-1 min-w-0 flex items-center gap-3 py-2">
        <span
          className={`w-14 shrink-0 text-center rounded-full px-2 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-wide ${isToday ? "text-ink" : "bg-cream-2 text-ink-3"}`}
          style={isToday ? { background: accentColor } : undefined}
        >
          {dayLabel(offset)}
        </span>

        {!hasSunWindow ? (
          <button
            type="button"
            onClick={onSetSunHours}
            className="flex-1 min-w-0 inline-flex items-center gap-1 bg-transparent border-0 p-0 text-left font-sans text-[11.5px] text-ink-3 hover:text-ink cursor-pointer transition-colors"
          >
            <SunIcon size={11} stroke={2} className="shrink-0" />
            <span className="truncate underline decoration-dotted underline-offset-2">Sun window unset</span>
          </button>
        ) : (
          <div className="flex-1 min-w-0 flex flex-col gap-1">
            <span className="inline-flex items-center gap-1 font-sans text-[11.5px] text-ink-3">
              <SunIcon size={11} stroke={2} className="shrink-0" />
              <span className="truncate">
                {sunHours == null ? "Sun pending" : `${formatSunHours(sunHours)} Sun`}
              </span>
            </span>
            {sunHours != null && windowHours != null && windowHours > 0 ? (
              <div
                className="w-full max-w-24 h-[3px] rounded-full bg-cream-2 overflow-hidden"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={Math.round(windowHours * 60)}
                aria-valuenow={Math.round(sunHours * 60)}
                title={`${formatSunHours(sunHours)} of ${formatSunHours(windowHours)} sun window`}
              >
                <div
                  className="h-full rounded-full bg-amber-400"
                  style={{ width: `${Math.min(100, Math.max(0, (sunHours / windowHours) * 100))}%` }}
                />
              </div>
            ) : null}
          </div>
        )}

        <div className="shrink-0">
          {offset <= 0 ? (
            // Today reflects a logged watering when present; otherwise it mirrors the
            // recommendation's loading/error state. Past days are pure history.
            isToday && !wateredSource && recommendationLoading ? (
              <WaterButtonSkeleton />
            ) : isToday && !wateredSource && recommendationError ? (
              <ErroredWaterPill />
            ) : (
              <WaterToggleButton
                pot={pot}
                offset={offset}
                waterState={isToday ? resolveWaterState(day, wateredSource) : pastWaterState(wateredSource)}
                animateIn={isToday}
              />
            )
          ) : offset === 1 ? (
            // Recommendation-only — tomorrow's logged state is irrelevant to the forecast.
            recommendationLoading ? (
              <WaterButtonSkeleton />
            ) : recommendationError ? (
              <ErroredWaterPill />
            ) : (
              <TomorrowStatusPill waterState={resolveWaterState(day, null)} />
            )
          ) : (
            <NotTrackedPill />
          )}
        </div>
      </div>
    </div>
  );
}

interface WaterToggleButtonProps {
  pot: Pot;
  /** Day offset from today; the watering entry is written for today + offset. */
  offset: number;
  waterState: WaterState;
  /** Fade the button in — used when it replaces a loading skeleton. */
  animateIn?: boolean;
}

/**
 * Inline toggle for an editable day (today or past). Tapping logs or removes a
 * user watering for that day; rain entries are read-only.
 */
function WaterToggleButton({ pot, offset, waterState, animateIn = false }: WaterToggleButtonProps) {
  const { setPotWaterEntryForDay } = usePots();
  const [busy, setBusy] = useState(false);

  if (waterState === "none") return null;

  const isLoggedByUser = waterState === "watered";
  const interactive = waterState !== "rained";

  const { label, Icon, classes } = waterButtonAppearance(waterState);

  const onToggle = async () => {
    if (busy || !interactive) return;
    setBusy(true);
    try {
      await setPotWaterEntryForDay({
        potId: pot.id,
        date: Temporal.Now.plainDateISO().add({ days: offset }).toString(),
        watered: !isLoggedByUser,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={busy || !interactive}
      aria-label={interactive ? (isLoggedByUser ? "Mark as not watered" : "Mark as watered") : label}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-sans text-[12.5px] font-medium transition-colors duration-150 ${classes} ${interactive ? "cursor-pointer" : "cursor-default"} ${animateIn ? "animate-[loc-fade_0.3s_ease]" : ""} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {busy ? (
        <span
          className="w-3.5 h-3.5 rounded-full inline-block border-[1.5px] border-current/30 border-t-current animate-spin shrink-0"
          aria-hidden="true"
        />
      ) : (
        <Icon size={13} stroke={2} className="shrink-0" />
      )}
      {label}
    </button>
  );
}

function TomorrowStatusPill({ waterState }: { waterState: WaterState }) {
  if (waterState === "none") return null;
  const needed = waterState === "needed";
  const Icon = needed ? DropletIcon : DropletOffIcon;
  const label = needed ? "Thirsty" : "No need";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-sans text-[12.5px] font-medium cursor-default animate-[loc-fade_0.3s_ease] ${needed ? "bg-water/25 text-water-ink/70" : "bg-cream-2 text-ink-3"}`}
    >
      <Icon size={13} stroke={2} className="shrink-0" />
      {label}
    </span>
  );
}

/** Pill-shaped placeholder shown while a day's watering recommendation loads. */
function WaterButtonSkeleton() {
  return <span className="skeleton block w-[88px] h-[30px] rounded-full" aria-hidden="true" />;
}

/** Pill shown when a day's watering recommendation failed to load. */
function ErroredWaterPill() {
  return (
    <span
      role="alert"
      aria-label="Recommendation unavailable"
      title="Recommendation unavailable"
      className="inline-flex items-center justify-center rounded-full px-3 py-1.5 cursor-default animate-[loc-fade_0.3s_ease] bg-terra text-paper"
    >
      <AlertCircleIcon size={15} stroke={2} className="shrink-0" />
    </span>
  );
}

/** Read-only marker for future days that have no recommendation or history. */
function NotTrackedPill() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-sans text-[12.5px] font-medium cursor-default bg-cream-2 text-ink-4">
      <EllipsisIcon size={13} stroke={2} className="shrink-0" />
      Not tracked
    </span>
  );
}

function waterButtonAppearance(state: WaterState): { label: string; Icon: typeof DropletIcon; classes: string } {
  switch (state) {
    case "needed":
      return { label: "Thirsty", Icon: DropletIcon, classes: "bg-water text-paper hover:brightness-95" };
    case "watered":
      return { label: "Watered", Icon: CheckCircleIcon, classes: "bg-aqua-soft text-aqua hover:brightness-95" };
    case "rained":
      return { label: "Rained", Icon: CheckCircleIcon, classes: "bg-cream-2 text-ink-3" };
    case "notWatered":
      return { label: "Not watered", Icon: DropletOffIcon, classes: "bg-cream-2 text-ink-3 hover:bg-line" };
    case "skip":
    default:
      return { label: "No need", Icon: DropletOffIcon, classes: "bg-cream-2 text-ink-3 hover:bg-line" };
  }
}

/** Badge label for a day offset: Today, Tmrw, or the short weekday name. */
function dayLabel(offset: number): string {
  if (offset === 0) return "Today";
  if (offset === 1) return "Tmrw";
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toLocaleDateString(undefined, { weekday: "short" });
}

/** Duration of the pot's sun window in hours, or null if unset/invalid. */
function sunWindowHours(start?: string, end?: string): number | null {
  if (!start || !end) return null;
  const toMin = (s: string) => {
    const [h, m] = s.split(":").map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
  };
  const diff = toMin(end) - toMin(start);
  return Number.isFinite(diff) && diff > 0 ? diff / 60 : null;
}

/**
 * State of today/tomorrow, which carry an AI recommendation. A logged watering
 * wins over the recommendation so the interactive control reflects reality —
 * "Watered" once done, not a stale "Thirsty".
 */
function resolveWaterState(day: WateringDay | null, wateredSource: "user" | "rain" | null): WaterState {
  // A logged watering settles the state even before the recommendation loads,
  // so today still shows its real state (e.g. "Watered") while `plan` is null.
  if (wateredSource === "user") return "watered";
  if (wateredSource === "rain") return "rained";
  if (!day) return "none";
  if (day.shouldWater) return "needed";
  return "skip";
}

/** State of a past day, driven purely by history (no recommendation exists). */
function pastWaterState(wateredSource: "user" | "rain" | null): WaterState {
  if (wateredSource === "user") return "watered";
  if (wateredSource === "rain") return "rained";
  return "notWatered";
}
